import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import type { Product } from '../types';
import Reveal from './Reveal';
import { foto } from '../imagenes';

/**
 * Encabezado de sección.
 *
 * Sigue el patrón del sitio: etiqueta roja muy espaciada, titular en serif
 * —normalmente con una parte en cursiva roja— y filete rojo debajo.
 */
export const SectionHead: React.FC<{
  eyebrow?: string;
  title: React.ReactNode;
  lead?: string;
  center?: boolean;
  light?: boolean;
  id?: string;
}> = ({ eyebrow, title, lead, center = false, light = false, id }) => (
  <Reveal className={`${center ? 'mx-auto text-center' : ''} max-w-2xl`}>
    {eyebrow && (
      <span className={`eyebrow-cdf mb-4 ${light ? 'text-cabina-cream' : ''}`}>{eyebrow}</span>
    )}
    <h2
      id={id}
      className={`font-serif text-fluid-h2 font-bold ${light ? 'text-white' : 'text-cabina-dark'}`}
    >
      {title}
    </h2>
    <span className={`rule-cdf mt-6 ${center ? 'mx-auto' : ''}`} />
    {lead && (
      <p className={`mt-6 leading-relaxed ${light ? 'text-white/70' : 'text-gray-600'}`}>{lead}</p>
    )}
  </Reveal>
);

/**
 * Migas de pan (el JSON-LD equivalente va aparte, en cada página).
 *
 * En versalitas espaciadas se leían como una ruta de sistema de archivos.
 * Aquí van en la serif del sitio, con la página actual en cursiva roja y un
 * rombo como separador: sigue siendo una ruta, pero pertenece a la marca.
 */
export const Breadcrumbs: React.FC<{
  items: { label: string; to?: string }[];
  light?: boolean;
}> = ({ items, light = false }) => (
  <nav
    aria-label="Ruta de navegación"
    className={`mb-8 flex flex-wrap items-center gap-x-1 font-serif text-[13px] md:text-sm ${
      light ? 'text-white/55' : 'text-cabina-ink/45'
    }`}
  >
    {items.map((item, i) => (
      <React.Fragment key={item.label}>
        {i > 0 && (
          <span
            className={`mx-2 text-[7px] ${light ? 'text-cabina-cream/50' : 'text-cabina-red/45'}`}
            aria-hidden="true"
          >
            ◆
          </span>
        )}
        {item.to ? (
          <Link
            to={item.to}
            className={`link-underline transition-colors ${
              light ? 'hover:text-cabina-cream' : 'hover:text-cabina-red'
            }`}
          >
            {item.label}
          </Link>
        ) : (
          <span
            aria-current="page"
            className={`italic font-semibold ${light ? 'text-cabina-cream' : 'text-cabina-red'}`}
          >
            {item.label}
          </span>
        )}
      </React.Fragment>
    ))}
  </nav>
);

/**
 * Bloque de respuesta directa.
 *
 * Existe para búsqueda con IA: un asistente que resuelve "mejor cabina de
 * fotos en Medellín" cita párrafos cortos que se entienden solos. Por eso la
 * pregunta va literal como encabezado y la respuesta no depende del resto de
 * la página para tener sentido.
 */
export const AnswerBlock: React.FC<{ question: string; answer: string; id?: string }> = ({
  question,
  answer,
  id,
}) => (
  <Reveal>
    <div className="relative overflow-hidden rounded-3xl bg-cabina-cream p-8 ring-1 ring-cabina-red/12 md:p-11">
      <span
        className="absolute -right-6 -top-10 font-serif text-[10rem] leading-none text-cabina-red/8"
        aria-hidden="true"
      >
        ”
      </span>
      <div className="relative z-10">
        <span className="eyebrow-cdf mb-4">Respuesta corta</span>
        <h2 id={id} className="font-serif text-2xl font-bold text-cabina-dark md:text-3xl">
          {question}
        </h2>
        <span className="rule-cdf mt-5" />
        <p className="mt-6 max-w-3xl text-lg leading-relaxed text-cabina-ink/75">{answer}</p>
      </div>
    </div>
  </Reveal>
);

/**
 * Tarjeta rectangular de producto, para retículas donde la circular no cabe
 * (recomendaciones dentro de una página, relacionados). Es un enlace real.
 */
export const ProductCard: React.FC<{ product: Product; eager?: boolean; index?: number }> = ({
  product,
  eager = false,
  index = 0,
}) => (
  <Reveal delay={(index % 4) * 70} className="h-full">
    <Link
      to={`/catalogo/${product.id}`}
      className="card-sheen group flex h-full flex-col overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-cabina-red/8 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:ring-cabina-red/30"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          {...foto(product.image, '(min-width: 1024px) 25vw, 50vw')}
          alt={`${product.name} — ${product.shortDescription}`}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
        />
        <span
          className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          aria-hidden="true"
        />
      </div>

      <div className="flex flex-grow flex-col p-4 md:p-6">
        <div className="mb-2.5 flex flex-wrap gap-1.5 md:mb-3">
          {product.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-cabina-cream px-2 py-0.5 text-[9px] font-semibold md:px-2.5 md:py-1 md:text-[10px] uppercase tracking-wide text-gray-500"
            >
              {tag}
            </span>
          ))}
        </div>
        <h3 className="font-serif text-base font-bold leading-snug text-cabina-dark transition-colors group-hover:text-cabina-red md:text-xl">
          {product.name}
        </h3>
        <p className="mt-1.5 flex-grow text-xs leading-relaxed text-gray-600 md:mt-2 md:text-sm">
          {product.shortDescription}
        </p>
        <span className="mt-4 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-cabina-red md:mt-5 md:text-[11px]">
          Ver detalles
          <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  </Reveal>
);

/** Acordeón de preguntas con marcado accesible nativo. */
export const FaqList: React.FC<{ faqs: { q: string; a: string }[] }> = ({ faqs }) => (
  <div className="divide-y divide-cabina-red/15 border-y border-cabina-red/15">
    {faqs.map((faq) => (
      <details key={faq.q} className="group py-5">
        <summary className="cursor-pointer list-none font-serif text-lg font-bold text-cabina-dark transition-colors marker:hidden group-open:text-cabina-red">
          <span className="flex items-start justify-between gap-4">
            {faq.q}
            <span
              className="mt-0.5 shrink-0 text-2xl leading-none text-cabina-red transition-transform duration-300 group-open:rotate-45"
              aria-hidden="true"
            >
              +
            </span>
          </span>
        </summary>
        <p className="mt-3 leading-relaxed text-gray-600">{faq.a}</p>
      </details>
    ))}
  </div>
);

/**
 * Encabezado de página con carga fotográfica.
 *
 * Las páginas internas tenían todas el mismo encabezado de puro texto sobre
 * crema, que en un negocio de fotografía es justo lo que no debe pasar. Aquí
 * el texto ocupa la mitad y la otra mitad son fotos reales.
 *
 * - `fan`   → tirillas verticales abanicadas (sirve cuando lo que hay que
 *             enseñar es el entregable, no el equipo).
 * - `stack` → una foto grande con dos piezas montadas encima en diagonal.
 */
export const PageHero: React.FC<{
  breadcrumbs: { label: string; to?: string }[];
  eyebrow: string;
  title: React.ReactNode;
  lead: string;
  actions?: React.ReactNode;
  photos: { src: string; alt: string }[];
  variant?: 'fan' | 'stack';
}> = ({ breadcrumbs, eyebrow, title, lead, actions, photos, variant = 'stack' }) => (
  <header className="relative overflow-hidden bg-cabina-cream bg-pattern pb-20 pt-10 md:pb-24 md:pt-14">
    <div
      className="absolute inset-0 bg-gradient-to-b from-cabina-cream via-white/70 to-cabina-cream"
      aria-hidden="true"
    />

    <div className="container relative z-10 mx-auto px-6">
      <Breadcrumbs items={breadcrumbs} />

      <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
        <Reveal>
          <div className="mb-5 flex items-center gap-3">
            <span className="rule-cdf" aria-hidden="true" />
            <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-cabina-ink/50">
              {eyebrow}
            </span>
          </div>
          <h1 className="font-serif text-fluid-h1 font-bold leading-[1.05] text-cabina-ink">
            {title}
          </h1>
          <p className="measure mt-6 text-fluid-lead leading-relaxed text-cabina-ink/70">{lead}</p>
          {actions && <div className="mt-9 flex flex-wrap gap-4">{actions}</div>}
        </Reveal>

        <Reveal delay={130} className="relative">
          {variant === 'fan' ? (
            <div className="flex items-center justify-center gap-0 py-6">
              {photos.slice(0, 3).map((photo, i) => (
                <img
                  key={photo.src}
                  {...foto(photo.src, '160px')}
                  alt={photo.alt}
                  loading="lazy"
                  decoding="async"
                  /* Se solapan y se inclinan como tirillas recién recogidas
                     de la impresora, no como una retícula. */
                  className={`photo-strip w-1/3 max-w-[10rem] ${
                    [
                      'z-10 rotate-[-11deg] translate-y-4',
                      'z-20 rotate-[3deg] -translate-x-4',
                      'z-10 rotate-[13deg] -translate-x-8 translate-y-6',
                    ][i]
                  }`}
                />
              ))}
            </div>
          ) : (
            <>
              <div
                className="absolute -right-8 -top-10 aspect-square w-2/3 rounded-full bg-cabina-red/8 blur-2xl"
                aria-hidden="true"
              />
              <figure className="relative z-10 m-0">
                <img
                  {...foto(photos[0].src, '(min-width: 1024px) 45vw, 100vw')}
                  alt={photos[0].alt}
                  loading="lazy"
                  decoding="async"
                  className="aspect-[5/4] w-full rounded-[2rem] object-cover shadow-2xl ring-1 ring-black/5"
                />
              </figure>
              {photos[1] && (
                <img
                  {...foto(photos[1].src, '160px')}
                  alt={photos[1].alt}
                  loading="lazy"
                  decoding="async"
                  className="absolute -bottom-8 -left-5 z-20 w-28 rounded-2xl object-cover shadow-xl ring-4 ring-white md:w-40"
                />
              )}
              {photos[2] && (
                <img
                  {...foto(photos[2].src, '112px')}
                  alt={photos[2].alt}
                  loading="lazy"
                  decoding="async"
                  className="photo-strip absolute -right-2 -top-6 z-20 w-20 rotate-[10deg] md:w-28"
                />
              )}
            </>
          )}
        </Reveal>
      </div>
    </div>
  </header>
);
